import type Experiments from '@wix/wix-experiments';
import type { ISiteContext, IServices } from '../../../../models';
import type { ManagedApps } from '../../../../services/managed-apps';
import { isAppSupportedInResponsiveEditors } from './editor-not-supported/api';
import { AppNotificationType, type AppNotification } from './types';

export async function getAppNotification({
  services,
  managedApps,
  app,
  siteContext,
  experiments,
}: {
  services: IServices;
  managedApps?: ManagedApps;
  app: {
    id: string;
    name: string;
    isPremiumSiteRequired: boolean;
    dependencies: {
      id: string;
      slug: string;
      name: string;
      isPublished: boolean;
    }[];
  };
  siteContext: ISiteContext;
  experiments: Experiments;
}): Promise<AppNotification | null> {
  const { supportedInEditorX, supportedInStudio } =
    await isAppSupportedInResponsiveEditors({
      services,
      appId: app.id,
      experiments,
    });

  if (
    (siteContext?.editorType === 'STUDIO' && !supportedInStudio) ||
    (siteContext?.editorType === 'EDITORX' && !supportedInEditorX)
  ) {
    return {
      type: AppNotificationType.EditorNotSupported,
      data: {
        appName: app.name,
        editorType: siteContext?.editorType,
      },
    };
  }

  if (
    app.isPremiumSiteRequired &&
    !siteContext.isPremium &&
    siteContext.metaSiteId
  ) {
    return {
      type: AppNotificationType.PremiumSiteRequired,
      data: {
        app: {
          id: app.id,
          name: app.name,
        },
      },
    };
  }

  const managedApp = managedApps?.findByAppId?.(app.id);
  const appDashboard = managedApp?.getAppDashboard?.();
  if (managedApp?.isAppPendingConfiguration?.() && appDashboard) {
    return {
      type: AppNotificationType.SetupIncomplete,
      data: {
        appId: app.id,
        dashboardUrl: appDashboard.url,
        isExternalDashboard: appDashboard.isExternal,
      },
    };
  }

  const publishedAppDependencies = app.dependencies.filter(
    (appDependency) => appDependency.isPublished,
  );
  if (
    managedApps &&
    publishedAppDependencies.length > 0 &&
    app.dependencies.every(
      (appDependency) => !managedApps.isAppInstalled(appDependency.id),
    )
  ) {
    return {
      type: AppNotificationType.DependenciesNotInstalled,
      data: {
        app,
        appDependencies: publishedAppDependencies,
      },
    };
  }

  const pendingEditorAppDependency = findPendingEditorAppDependency({
    appDependencies: app.dependencies,
    managedApps,
  });

  if (
    pendingEditorAppDependency &&
    !appHasInstalledDependencyNotPendingEditor({
      appDependencies: app.dependencies,
      managedApps,
    })
  ) {
    return {
      type: AppNotificationType.DependencyPendingEditor,
      data: {
        app,
        appDependency: pendingEditorAppDependency,
      },
    };
  }

  return null;
}

function appHasInstalledDependencyNotPendingEditor({
  appDependencies,
  managedApps,
}: {
  appDependencies: {
    id: string;
    slug: string;
    name: string;
  }[];
  managedApps: ManagedApps | undefined;
}): boolean {
  return appDependencies.some((appDependency) => {
    return Boolean(
      managedApps &&
        managedApps.isAppInstalled(appDependency.id) &&
        !managedApps.findByAppId(appDependency.id).isAppPendingEditor(),
    );
  });
}

function findPendingEditorAppDependency({
  managedApps,
  appDependencies,
}: {
  managedApps: ManagedApps | undefined;
  appDependencies: {
    id: string;
    slug: string;
    name: string;
  }[];
}):
  | {
      id: string;
      slug: string;
      name: string;
    }
  | undefined {
  return appDependencies.find((appDependency) => {
    return Boolean(
      managedApps &&
        managedApps.findByAppId(appDependency.id).isAppPendingEditor(),
    );
  });
}
